import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { QueryKey } from '../../enums/QueryKey';
import { exportTotalsData, getTotals, getTotalsExportData } from '../../services/totals';
import { getStartAndEndOfAMonth } from '../../utils/dateUtils';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
// import { useDispatchSuccessNotification } from '../useDispatchSuccessNotification';
import { downloadBlobFile } from '../../utils/fileUtils';
import dayjs, { Dayjs } from '@utils/dayjs';

export function useTotals({ date, workingUnitLocation }: { date: string | Date | Dayjs; workingUnitLocation: number }) {
    const dateFrom = dayjs(date).startOf('month');
    const dateTo = dayjs(date).endOf('month');

    const { data, isSuccess } = useQuery({
        queryKey: [QueryKey.Totals, { date, workingUnitLocation, dateFrom, dateTo }],
        queryFn: () => getTotals({ dateFrom, dateTo, workingUnitLocation }),
        enabled: !!workingUnitLocation && !!date,
        initialData: [],
    });

    return { totals: data, isSuccess };
}

export function useTotalsExportData({
    date,
    totalsType,
    fetchImmediately = false,
}: {
    date: string | Date | Dayjs;
    totalsType: 'bidding' | 'corrected';
    fetchImmediately?: boolean;
}) {
    const { dateFrom, dateTo } = getStartAndEndOfAMonth(date);

    const { data, refetch } = useQuery({
        queryKey: [
            QueryKey.TotalsExport,
            {
                dateFrom: dateFrom.format(DATE_FORMATS.ISO_DATETIME),
                dateTo: dateTo.format(DATE_FORMATS.ISO_DATETIME),
                totalsType,
            },
            dateFrom,
            dateTo,
        ],
        queryFn: () => getTotalsExportData({ dateFrom, dateTo, totalsType }),
        enabled: !!date && !!totalsType && fetchImmediately,
    });

    return { data, refetch };
}

export function useExportTotalsData() {
    const { t } = useTranslation();
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    // const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation({
        mutationFn: exportTotalsData,
        onError: (error: Error) => {
            dispatchErrorNotification({ message: t(error.message) });
        },
        onSuccess: (data) => {
            // dispatchSuccessNotification({ message: t('notification.fileDownloaded') });
            downloadBlobFile(data);
        },
    });
}
